import React, { useEffect } from 'react';
import { useSiteContent } from '../context/SiteContentContext';
import { AGENCY_CONFIG } from '../config/agency';
import { Apartment } from '../types';

interface HeadSEOProps {
  activeTab: 'home' | 'sales' | 'rentals' | 'services' | 'team' | 'contacts' | string;
  selectedApartment?: Apartment | null;
}

export const HeadSEO: React.FC<HeadSEOProps> = ({ activeTab, selectedApartment }) => {
  const { content } = useSiteContent();

  useEffect(() => {
    const siteBaseUrl = (content.seo?.siteUrl || (typeof window !== 'undefined' ? window.location.origin : 'https://varna-imoti.bg')).replace(/\/$/, '');

    const tabPaths: Record<string, string> = {
      home: '/',
      all: '/oferti',
      sales: '/prodazhbi',
      rentals: '/naemi',
      services: '/uslugi',
      team: '/ekip',
      contacts: '/kontakti',
      about: '/za-nas',
      terms: '/obshti-uslovia',
      privacy: '/poveritelnost',
    };

    // Helper to update or create meta tag
    const updateMeta = (name: string, contentVal: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let tag = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', contentVal || '');
    };

    // 0. Dynamic Favicon
    const faviconUrl = content.branding?.faviconUrl || '/imotencenterlogo-cropped.png';
    const updateFavicon = (rel: string, id: string) => {
      let fav = document.getElementById(id) as HTMLLinkElement | null;
      if (!fav) {
        fav = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      }
      if (!fav) {
        fav = document.createElement('link');
        fav.rel = rel;
        fav.id = id;
        document.head.appendChild(fav);
      }
      fav.href = faviconUrl;
    };
    updateFavicon('icon', 'app-favicon');
    updateFavicon('apple-touch-icon', 'app-apple-favicon');

    // Case 1: Specific Property is currently open (modal / deep link)
    if (selectedApartment) {
      const propTitle = `${selectedApartment.title} | ${content.branding?.siteTitle || 'Варна Имотен Център'}`;
      const propDesc = selectedApartment.description?.slice(0, 160) || `${selectedApartment.price} € - ${selectedApartment.type || 'Имот'} във Варна, ${selectedApartment.neighborhood}.`;
      const propImg = selectedApartment.image || selectedApartment.gallery?.[0] || content.seo?.defaultOgImage || '/hero.webp';
      let fullPropImg = propImg;
      if (!fullPropImg.startsWith('http://') && !fullPropImg.startsWith('https://')) {
        fullPropImg = `${siteBaseUrl}/${fullPropImg.replace(/^\//, '')}`;
      }
      const propCanonical = `${siteBaseUrl}/imoti/${encodeURIComponent(selectedApartment.id)}`;

      document.title = propTitle;
      updateMeta('description', propDesc);
      updateMeta('og:title', propTitle, true);
      updateMeta('og:description', propDesc, true);
      updateMeta('og:image', fullPropImg, true);
      updateMeta('og:image:secure_url', fullPropImg, true);
      updateMeta('og:image:width', '1200', true);
      updateMeta('og:image:height', '630', true);
      updateMeta('og:url', propCanonical, true);
      updateMeta('twitter:card', 'summary_large_image');
      updateMeta('twitter:title', propTitle);
      updateMeta('twitter:description', propDesc);
      updateMeta('twitter:image', fullPropImg);

      let canonicalLinks = document.querySelectorAll('link[rel="canonical"]');
      if (canonicalLinks.length > 1) {
        canonicalLinks.forEach((link, idx) => {
          if (idx > 0) link.remove();
        });
      }
      let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', propCanonical);
      return;
    }

    if (!content.seo || !content.seo.pages) return;

    const legalFallbacks: Record<string, any> = {
      terms: {
        metaTitle: 'Общи условия за ползване | Варна Имотен Център',
        metaDescription: 'Официални общи условия за ползване на сайта и посредническите услуги на „Имотен център Варна“ ЕООД съгласно българското законодателство.',
        focusKeyword: 'общи условия, имотен център варна',
        urlPath: '/obshti-uslovia'
      },
      privacy: {
        metaTitle: 'Политика за защита на личните данни (GDPR) | Варна Имотен Център',
        metaDescription: 'Политика за защита на личните данни и поверителност съгласно Регламент (ЕС) 2016/679 (GDPR) на „Имотен център Варна“ ЕООД.',
        focusKeyword: 'политика за поверителност, лични данни, GDPR, варна имотен център',
        urlPath: '/poveritelnost'
      }
    };

    const pageConfig = content.seo.pages[activeTab] || legalFallbacks[activeTab] || content.seo.pages.home;
    if (!pageConfig) return;

    // 1. Meta Title
    const title = pageConfig.metaTitle || `${content.branding?.siteTitle || 'Варна Имотен Център'} - Недвижими Имоти`;
    document.title = title;

    // 2. Meta Description & Keywords
    updateMeta('description', pageConfig.metaDescription || '');
    const keywords = [pageConfig.focusKeyword, pageConfig.secondaryKeywords].filter(Boolean).join(', ');
    updateMeta('keywords', keywords);
    updateMeta('robots', pageConfig.noIndex ? 'noindex, nofollow' : 'index, follow');

    // 3. Open Graph Tags
    const ogTitle = pageConfig.ogTitle || pageConfig.metaTitle || title;
    const ogDesc = pageConfig.ogDescription || pageConfig.metaDescription || '';
    const rawOgImg = pageConfig.ogImage || content.seo?.defaultOgImage || '/hero.webp';
    let ogImg = rawOgImg;
    if (rawOgImg && !rawOgImg.startsWith('http://') && !rawOgImg.startsWith('https://')) {
      ogImg = `${siteBaseUrl}/${rawOgImg.replace(/^\//, '')}`;
    }

    // Dynamic Canonical URL calculation:
    // Uses configured canonicalUrl if present; otherwise auto-generates matching ${siteBaseUrl}${tabPath}
    const expectedPath = tabPaths[activeTab] || (pageConfig.urlPath || '/');
    const dynamicCanonical = `${siteBaseUrl}${expectedPath === '/' ? '/' : expectedPath}`;
    const canonical = pageConfig.canonicalUrl && pageConfig.canonicalUrl.trim()
      ? pageConfig.canonicalUrl.trim()
      : dynamicCanonical;

    updateMeta('og:title', ogTitle, true);
    updateMeta('og:description', ogDesc, true);
    updateMeta('og:image', ogImg, true);
    updateMeta('og:image:secure_url', ogImg, true);
    updateMeta('og:image:width', '1200', true);
    updateMeta('og:image:height', '630', true);
    updateMeta('og:url', canonical, true);
    updateMeta('og:type', 'website', true);
    updateMeta('og:site_name', content.seo.siteName || content.branding?.siteTitle || 'Варна Имотен Център', true);

    // 4. Twitter Card
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', ogTitle);
    updateMeta('twitter:description', ogDesc);
    updateMeta('twitter:image', ogImg);

    // 5. Google Search Console Verification Meta Tag
    if (content.seo?.tracking?.googleSearchConsoleVerification) {
      updateMeta('google-site-verification', content.seo.tracking.googleSearchConsoleVerification);
    }

    // 6. Canonical Link
    let canonicalLinks = document.querySelectorAll('link[rel="canonical"]');
    if (canonicalLinks.length > 1) {
      canonicalLinks.forEach((link, idx) => {
        if (idx > 0) link.remove();
      });
    }
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonical) {
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', canonical);
    }

    // 7. JSON-LD Structured Data (RealEstateAgent / LocalBusiness Schema)
    if (content.seo.structuredDataEnabled !== false) {
      let scriptTag = document.getElementById('seo-structured-data') as HTMLScriptElement | null;
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.id = 'seo-structured-data';
        scriptTag.type = 'application/ld+json';
        document.head.appendChild(scriptTag);
      }

      const structuredData = {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'RealEstateAgent',
            '@id': `${content.seo.siteUrl || 'https://varna-imoti.bg'}#organization`,
            name: content.seo.siteName || content.branding?.siteTitle || 'Варна Имотен Център',
            url: content.seo.siteUrl || 'https://varna-imoti.bg',
            logo: content.branding?.logoUrl || `${content.seo.siteUrl || 'https://varna-imoti.bg'}/logo.svg`,
            image: ogImg,
            description: pageConfig.metaDescription,
            telephone: content.contactsPage?.phone1 || content.navigation?.topBar?.phone || AGENCY_CONFIG.phone,
            email: content.contactsPage?.email || content.navigation?.topBar?.email || AGENCY_CONFIG.email,
            address: {
              '@type': 'PostalAddress',
              streetAddress: content.contactsPage?.address || AGENCY_CONFIG.address,
              addressLocality: 'Варна',
              postalCode: '9002',
              addressCountry: 'BG'
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: '43.2141',
              longitude: '27.9147'
            },
            openingHoursSpecification: [
              {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
                opens: '09:00',
                closes: '18:30'
              },
              {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: ['Saturday'],
                opens: '10:00',
                closes: '15:00'
              }
            ],
            sameAs: [
              content.navigation?.topBar?.facebookUrl,
              content.navigation?.topBar?.instagramUrl
            ].filter(Boolean)
          },
          {
            '@type': 'WebPage',
            '@id': `${canonical}#webpage`,
            url: canonical,
            name: pageConfig.metaTitle,
            description: pageConfig.metaDescription,
            isPartOf: {
              '@id': `${content.seo.siteUrl || 'https://varna-imoti.bg'}#organization`
            },
            primaryImageOfPage: {
              '@type': 'ImageObject',
              url: ogImg
            }
          }
        ]
      };

      scriptTag.text = JSON.stringify(structuredData);
    }
  }, [activeTab, selectedApartment, content.seo, content.branding, content.contactsPage, content.navigation]);

  return null;
};

export default HeadSEO;
